import { IconButton } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  FormProvider,
  Iconify,
  MenuPopover,
  RowActions as MenuRowActions,
  useMenuPopover,
} from '@app/components';
import { useDialog, useRequestState } from '@app/hooks';
import { Strings } from '@app/constants';
import { useAuthRole } from '@app/utils';
import { updateCandidateStatusSchema } from '@app/modules/candidates';
import { ConfirmationDialog } from '@app/components/dialog';
import { AddEmployeePage } from './add-employee/add-employee-page';
import { selectUpdateEmployeeStatusState } from '../selectors';
import { IEmployee } from '../types';
import { updateEmployeeStatus } from '../slice';

interface Props {
  row: IEmployee;
}

const RowActions = ({ row }: Props) => {
  const menuPopover = useMenuPopover();
  const editDialog = useDialog();
  const activeInactiveDialog = useDialog();
  const { isManager } = useAuthRole();

  const methods = useForm({
    resolver: yupResolver(updateCandidateStatusSchema) as any,
  });
  const newStatus =
    row.status === Strings.status.active ? Strings.status.inactive : Strings.status.active;
  const actions = [
    {
      label: Strings.rowActions.edit,
      callback: editDialog.show,
      disabled: row.status === Strings.status.inactive,
    },
    { label: newStatus, callback: activeInactiveDialog.show },
  ];
  const dispatch = useDispatch();
  const { loading } = useRequestState({
    stateSelector: selectUpdateEmployeeStatusState,
    onSuccess: activeInactiveDialog.hide,
    successMessageShown: true,
    errorShown: true,
  });

  const updateEmployeeStatusHandle = (reason: string) => {
    dispatch(
      updateEmployeeStatus({
        row,
        status: newStatus,
        reason,
      })
    );
  };
  return (
    <>
      <IconButton onClick={menuPopover.show} disabled={isManager}>
        <Iconify icon="eva:more-vertical-fill" />
      </IconButton>

      <MenuPopover
        open={menuPopover.rowMenuAnchorElement}
        arrow="right-top"
        sx={{ width: 180 }}
        onClose={menuPopover.hide}
      >
        <MenuRowActions actions={actions} onClickAction={menuPopover.hide} />
      </MenuPopover>
      <FormProvider methods={methods}>
        <ConfirmationDialog
          title={`${Strings.common.activeInactiveTitle(row.status === Strings.status.active)}  ${
            Strings.addCandidate.employee
          }`}
          confirmationButtonTitle={Strings.button.confirm}
          caption={Strings.common.activeInactiveCaption(
            row.status === Strings.status.active,
            Strings.addCandidate.employee
          )}
          loading={loading}
          visible={activeInactiveDialog.visible}
          onClickConfirm={updateEmployeeStatusHandle}
          onClose={activeInactiveDialog.hide}
        />
      </FormProvider>

      {editDialog.visible && <AddEmployeePage open onClose={editDialog.hide} row={row} />}
    </>
  );
};

export { RowActions };
